import { z } from "zod";
import { isoAndUnixZodSchema } from "../date-time/iso-and-unix";
import { productIdZodSchema } from "../products/product-id";
import { providerIdZodSchema } from "../providers/provider-id";
import { toTranslatableString } from "../translations/translation";
import { productFormCheckboxAnswerZodObject } from "./fields/checkbox-field/product-form-field.checkbox-answer";
import { productFormFieldTypeZodEnum } from "./fields/product-form-field";
import { productFormFieldIdZodSchema } from "./fields/product-form-field-id";
import { productFormSelectAnswerZodObject } from "./fields/select-field/product-form-field.select-answer";
import { productFormTextAnswerZodObject } from "./fields/text-field/product-form-field.text-answer";
import { productFormAnswerIdZodSchema } from "./product-form-answer-id";
import { productFormIdZodSchema } from "./product-form-id";

const cart__productFormUnfilledFieldAnswerZodObject = z.object({
    _id: productFormFieldIdZodSchema,
    _type: z.literal("PRODUCT_FORM_FIELD_ANSWER"),
    type: z.literal("UNFILLED"),
    originalType: productFormFieldTypeZodEnum,
    required: z.boolean(),
});
type Cart_ProductFormUnfilledFieldAnswer = z.infer<
    typeof cart__productFormUnfilledFieldAnswerZodObject
>;

const _cart__productFormAnswerBaseZodObject = z.object({
    _id: productFormAnswerIdZodSchema,
    _type: z.literal("CART__PRODUCT_FORM_ANSWER"),
    _createdAt: isoAndUnixZodSchema,
    productId: productIdZodSchema,
    providerId: providerIdZodSchema,
    productFormId: productFormIdZodSchema,
    // sortOrder: z.array(productFormFieldIdZodSchema),
});

const cartFieldAnswerZodObject = z.union([
    productFormCheckboxAnswerZodObject,
    productFormTextAnswerZodObject,
    productFormSelectAnswerZodObject,
    cart__productFormUnfilledFieldAnswerZodObject,
]);

type Cart_FieldAnswer = z.infer<typeof cartFieldAnswerZodObject>;
type Cart_FieldAnswerType = Cart_FieldAnswer["type"];

const cart__productFormAnswerWorkingCopyZodObject =
    _cart__productFormAnswerBaseZodObject
        .merge(
            z.object({
                answers: z.array(cartFieldAnswerZodObject),
            }),
        )
        .refine(
            data => {
                const unfilled = data.answers.filter(
                    field => field.type === "UNFILLED",
                );
                if (unfilled.some(item => item.required)) return false;
                return true;
            },
            data => ({
                message: toTranslatableString({
                    sv: "Fyll i alla obligatoriska fält.",
                    en: "Fill in all required fields.",
                }),
                params: {
                    unfilled: data.answers
                        .filter(field => field.type === "UNFILLED")
                        .map(field => field._id),
                    unfilledRequired: data.answers
                        .filter(
                            field =>
                                field.type === "UNFILLED" && field.required,
                        )
                        .map(field => field._id),
                },
                path: ["fields"],
            }),
        );

const cart__productFormAnswerZodObject = _cart__productFormAnswerBaseZodObject
    .merge(
        z.object({
            answers: z.array(
                z.union([
                    productFormCheckboxAnswerZodObject,
                    productFormTextAnswerZodObject,
                    productFormSelectAnswerZodObject,
                ]),
            ),
        }),
    )
    .refine(
        data => {
            const required = data.answers.filter(field => field.required);
            if (data.answers.length < required.length) return false;
            return true;
        },
        data => ({
            message: toTranslatableString({
                sv: "Antalet svar stämmer inte överens med antalet fält.",
                en: "The number of answers does not match the number of fields.",
            }),
            params: {
                // sortOrder: data.sortOrder,
                required: data.answers
                    .filter(field => field.required)
                    .map(field => field._id),
                requiredCount: data.answers.filter(field => field.required)
                    .length,
                fieldCount: data.answers.length,
            },
            path: ["answers"],
        }),
    );

type Cart_ProductFormAnswerWorkingCopy = z.infer<
    typeof cart__productFormAnswerWorkingCopyZodObject
>;
type Cart_ProductFormAnswer = z.infer<typeof cart__productFormAnswerZodObject>;

export {
    cart__productFormAnswerWorkingCopyZodObject,
    cart__productFormAnswerZodObject,
    cart__productFormUnfilledFieldAnswerZodObject,
};

export type {
    Cart_FieldAnswer,
    Cart_FieldAnswerType,
    Cart_ProductFormAnswer,
    Cart_ProductFormAnswerWorkingCopy,
    Cart_ProductFormUnfilledFieldAnswer,
};
